/**
 * Mixin holding the shared chip-editing core behind every "type a value, get a
 * chip" tag input (email_tags_controller.js, blog_tag_chips_controller.js).
 *
 * Interaction contract provided to every host controller:
 *   - Enter or comma commits the typed value as a chip
 *   - the chip's × button removes that chip
 *   - blur commits any pending typed text
 *   - Backspace on an empty input removes the last chip
 *   - the hidden field is kept in sync (joined with `valueSeparator`) and a
 *     bubbling `input` event is dispatched on it after every change, because
 *     programmatic value writes fire no native event and outside listeners
 *     (card-dirty on the enclosing form, blog-seo-preview) depend on one.
 *
 * Values are trimmed + lowercased + de-duplicated before becoming a chip.
 * Chips are built with DOM APIs (textContent) so a value can never inject
 * markup; only the static remove-icon uses innerHTML.
 *
 * Required targets on the host controller: `hidden`, `input`. Declare
 * `static targets` on the host rather than here so each controller's target
 * contract stays visible in the file the views point at.
 *
 * Usage:
 *   import { TagChipsMixin } from "../lib/tag_chips_mixin"
 *   export default class extends TagChipsMixin(Controller) {
 *     static targets = ["hidden", "input"]
 *     get chipNamespace() { return "gl-email-tags" }
 *     get valueSeparator() { return "\n" }
 *   }
 *
 * Configuration hooks (override on the host controller):
 *   - chipNamespace (required) — BEM block for chip markup, e.g. "gl-email-tags";
 *     yields `__chip`, `__chip-label`, `__chip-remove` element classes
 *   - valueSeparator (required) — how values are joined into the hidden field
 *   - removeAction — data-action for a generated chip's × button; defaults to
 *     `<identifier>#remove`, which is what every host wants
 *   - valueAllowed(value) — client-side validation; default accepts everything
 *   - showValidationFeedback() / clearValidationFeedback() — paired no-ops a
 *     host overrides to surface (and retract) a rejection hint
 */
export function TagChipsMixin(SuperClass) {
  return class extends SuperClass {
    // ---- configuration -----------------------------------------------------

    get chipNamespace() {
      throw new Error("TagChipsMixin: host controller must define chipNamespace")
    }

    get valueSeparator() {
      throw new Error("TagChipsMixin: host controller must define valueSeparator")
    }

    get removeAction() {
      return `${this.identifier}#remove`
    }

    /** @returns {boolean} whether `value` may become a chip. */
    valueAllowed(_value) {
      return true
    }

    /** Called when a typed value was rejected by valueAllowed(). */
    showValidationFeedback() {}

    /** Called whenever no rejection is outstanding (accepted, blank, or duplicate). */
    clearValidationFeedback() {}

    // ---- Stimulus actions --------------------------------------------------

    keydown(event) {
      if (event.key === "Enter" || event.key === ",") {
        event.preventDefault()
        this.addCurrentValue()
      } else if (event.key === "Backspace" && this.inputTarget.value === "") {
        this.removeLastChip()
      }
    }

    commit() {
      this.addCurrentValue()
    }

    remove(event) {
      event.preventDefault()
      event.target.closest(`.${this.chipNamespace}__chip`)?.remove()
      this.syncHidden()
    }

    focusInput() {
      this.inputTarget.focus()
    }

    // ---- chip editing core -------------------------------------------------

    addCurrentValue() {
      const value = this.inputTarget.value.trim().toLowerCase()
      this.inputTarget.value = ""

      if (!value || this.chipValues().includes(value)) {
        this.clearValidationFeedback()
        this.syncHidden()
        return
      }
      if (!this.valueAllowed(value)) {
        this.showValidationFeedback()
        this.syncHidden()
        return
      }

      this.clearValidationFeedback()
      this.appendChip(value)
      this.syncHidden()
    }

    removeLastChip() {
      const chips = this.element.querySelectorAll(`.${this.chipNamespace}__chip`)
      chips[chips.length - 1]?.remove()
      this.syncHidden()
    }

    appendChip(value) {
      const chip = document.createElement("span")
      chip.className = `${this.chipNamespace}__chip`

      const label = document.createElement("span")
      label.className = `${this.chipNamespace}__chip-label`
      label.textContent = value

      const button = document.createElement("button")
      button.type = "button"
      button.className = `${this.chipNamespace}__chip-remove`
      // Matches the server-rendered chip twin (_tag_chips.html.haml):
      // gl_tooltip_attrs('Remove tag') emits both attributes.
      button.setAttribute("aria-label", "Remove tag")
      button.setAttribute("data-gl-tooltip", "Remove tag")
      button.dataset.action = this.removeAction
      button.innerHTML = '<i class="ph ph-x"></i>'

      chip.append(label, button)
      this.inputTarget.insertAdjacentElement("beforebegin", chip)
    }

    /** Current chip labels, in DOM order — the single source of truth for the hidden field. */
    chipValues() {
      return Array.from(this.element.querySelectorAll(`.${this.chipNamespace}__chip-label`))
        .map((el) => el.textContent.trim())
        .filter(Boolean)
    }

    syncHidden() {
      this.hiddenTarget.value = this.chipValues().join(this.valueSeparator)
      this.hiddenTarget.dispatchEvent(new Event("input", { bubbles: true }))
    }
  }
}
